import i18n from '@/i18n';
import { create } from 'zustand';
import { Layer } from '@/types/api';
import layerService from '@/services/layer.service';

interface LayerVisibility {
  [layerId: number]: boolean;
}

interface LabelVisibility {
  [layerId: number]: boolean;
}

interface LayerFavorites {
  [layerId: number]: boolean;
}

/** Layers grouped by subMenu within a menu category */
export interface SubMenuGroup {
  subMenuId: number;
  subMenuName: string;
  layers: Layer[];
}

interface LayerState {
  allLayers: Layer[];
  borders: Layer[];
  thematics: Layer[];
  userOverlays: Layer[];
  pois: Layer[];
  labelLayers: Layer[];
  confLayers: Layer[];

  /** Thematic and POI layers grouped by subMenu */
  thematicGroups: SubMenuGroup[];
  poiGroups: SubMenuGroup[];

  visibility: LayerVisibility;
  labelVisibility: LabelVisibility;
  favorites: LayerFavorites;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadAllLayers: () => Promise<void>;
  loadLabelLayers: () => Promise<void>;
  toggleVisibility: (layerId: number) => void;
  setLayerVisible: (layerId: number, visible: boolean) => void;
  toggleLabelVisibility: (layerId: number) => void;
  toggleFavorite: (layerId: number) => void;
  clearError: () => void;
}

function groupBySubMenu(layers: Layer[]): SubMenuGroup[] {
  const groups: Map<number, SubMenuGroup> = new Map();
  for (const layer of layers) {
    const subMenuId = layer.subMenu?.id ?? 0;
    const subMenuName = layer.subMenu?.name ?? i18n.t('layers.other');
    if (!groups.has(subMenuId)) {
      groups.set(subMenuId, { subMenuId, subMenuName, layers: [] });
    }
    groups.get(subMenuId)!.layers.push(layer);
  }
  return Array.from(groups.values());
}

export const useLayerStore = create<LayerState>((set) => ({
  allLayers: [],
  borders: [],
  thematics: [],
  userOverlays: [],
  pois: [],
  labelLayers: [],
  confLayers: [],
  thematicGroups: [],
  poiGroups: [],
  visibility: {},
  labelVisibility: {},
  favorites: {},
  isLoading: false,
  error: null,

  loadAllLayers: async () => {
    set({ isLoading: true, error: null });
    try {
      const layers = await layerService.getAllUserLayers();

      const borders: Layer[] = [];
      const thematics: Layer[] = [];
      const userOverlays: Layer[] = [];
      const pois: Layer[] = [];
      const confLayers: Layer[] = [];
      const labelLayers: Layer[] = [];

      for (const layer of layers) {
        const menuName = layer.menu?.name?.toLowerCase() ?? '';
        if (menuName.includes('administrative') || menuName.includes('border')) {
          borders.push(layer);
        } else if (menuName.includes('thematic')) {
          thematics.push(layer);
        } else if (menuName.includes('user') || menuName.includes('overlay')) {
          userOverlays.push(layer);
        } else if (menuName.includes('poi')) {
          pois.push(layer);
        } else if (menuName.includes('label')) {
          labelLayers.push(layer);
        } else if (menuName.includes('conf') || menuName.includes('drawing')) {
          confLayers.push(layer);
        } else {
          // Default to user overlays
          userOverlays.push(layer);
        }
      }

      // Sort each group by orderInMenu
      const sort = (a: Layer, b: Layer) => (a.orderInMenu ?? 0) - (b.orderInMenu ?? 0);
      borders.sort(sort);
      thematics.sort(sort);
      userOverlays.sort(sort);
      pois.sort(sort);
      confLayers.sort(sort);
      labelLayers.sort(sort);

      set({
        allLayers: layers,
        borders,
        thematics,
        userOverlays,
        pois,
        labelLayers,
        confLayers,
        thematicGroups: groupBySubMenu(thematics),
        poiGroups: groupBySubMenu(pois),
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || i18n.t('layers.load_failed'),
        isLoading: false,
      });
    }
  },

  loadLabelLayers: async () => {
    try {
      // Label layers have menu ID 9 in the legacy system
      const labels = await layerService.getLayersForMenu(9);
      set({ labelLayers: labels });
    } catch {
      // Label layers are optional, don't set error
    }
  },

  toggleVisibility: (layerId: number) => {
    set((state) => ({
      visibility: {
        ...state.visibility,
        [layerId]: !state.visibility[layerId],
      },
    }));
  },

  setLayerVisible: (layerId: number, visible: boolean) => {
    set((state) => ({
      visibility: {
        ...state.visibility,
        [layerId]: visible,
      },
    }));
  },

  toggleLabelVisibility: (layerId: number) => {
    set((state) => ({
      labelVisibility: {
        ...state.labelVisibility,
        [layerId]: !state.labelVisibility[layerId],
      },
    }));
  },

  toggleFavorite: (layerId: number) => {
    set((state) => ({
      favorites: {
        ...state.favorites,
        [layerId]: !state.favorites[layerId],
      },
    }));
  },

  clearError: () => set({ error: null }),
}));

export default useLayerStore;
